import React, { useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import axios from 'axios';
import { useUser } from '../../utils/auth';
import { X, InfoIcon } from 'lucide-react';
import { FaHeart, FaRegHeart, FaCheckCircle } from 'react-icons/fa';
import { ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ApplicationForm from '../ApplicationForm';
import countryData from '../../utils/country.json';
import { useAppliedPrograms } from '../course-cards';

// Modal component
const Modal = ({ isOpen, onClose, children, maxWidth = "max-w-5xl" }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full ${maxWidth} bg-white rounded-lg shadow-xl`} onClick={e => e.stopPropagation()}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Create a context for saved programs
const SavedProgramsContext = createContext({
  savedPrograms: {},
  setSavedStatus: () => {}
});

// Custom hook for using the saved programs context
const useSavedPrograms = () => {
  return useContext(SavedProgramsContext);
};

// SaveHeart component for saving/unsaving programs
const SaveHeart = ({ programId, size = "lg" }) => {
  const { user, isAuthenticated } = useUser();
  const { savedPrograms, setSavedStatus } = useSavedPrograms();
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginTooltip, setShowLoginTooltip] = useState(false);
  const navigate = useNavigate();
  
  const isSaved = savedPrograms[programId] || false;

  // Determine if user is truly authenticated (has valid token and user object)
  const isUserAuthenticated = useMemo(() => {
    const token = localStorage.getItem('token');
    return isAuthenticated && !!user?.id && !!token;
  }, [user, isAuthenticated]);

  // Function to save or unsave program
  const toggleSave = async (e) => {
    e.stopPropagation();
    
    if (!isUserAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    if (isLoading) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (isSaved) {
        // Remove the program
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/remove-program`,
          { userId: user.id, programId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedStatus(programId, false);
      } else {
        // Save the program
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/save-program`,
          { userId: user.id, programId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedStatus(programId, true);
      }
    } catch (error) {
      console.error('Error toggling save status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  return (
    <div className="relative">
      <button
        onClick={toggleSave}
        disabled={isLoading}
        onMouseEnter={() => !isUserAuthenticated && setShowLoginTooltip(true)}
        onMouseLeave={() => setShowLoginTooltip(false)}
        className={`focus:outline-none transition-colors ${isLoading ? 'opacity-50' : 'hover:opacity-80'}`}
      >
        {isUserAuthenticated && isSaved ? (
          <FaHeart className={`${sizeClasses[size]} text-red-500`} />
        ) : (
          <FaRegHeart className={`${sizeClasses[size]} ${isUserAuthenticated ? 'text-gray-500 hover:text-red-500' : 'text-gray-400'}`} />
        )}
      </button>
      
      {showLoginTooltip && (
        <div className="absolute -top-10 right-0 bg-gray-800 text-white text-xs py-1 px-2 rounded shadow-lg whitespace-nowrap z-10">
          Login to save
          <div className="absolute bottom-0 right-2 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
        </div>
      )}
    </div>
  );
};

const UniversityCard = ({ card }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Utility function to check if the text is truncated
  const isTextTruncated = (element) => {
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
  };

  const TruncatedTextWithHover = ({ text, className }) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      if (textRef.current) {
        setIsTruncated(isTextTruncated(textRef.current));
      }
    }, [text]);

    return (
      <div className="relative group">
        <p
          ref={textRef}
          className={`truncate ${className}`}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </p>
        {isTruncated && (
          <div className="absolute z-10 bg-white border border-gray-300 p-2 shadow-lg rounded-md text-sm text-gray-700 hidden group-hover:block">
            {text}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-shadow w-full">
        <div className="flex items-start gap-3 mb-4">
          <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
            <img src={card.dgimgLink || card.imgLink}  alt={card.universityName.toUpperCase()} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="relative group w-full">
              <h3 className="text-sm lg:text-lg font-medium text-black truncate w-full">
                {card.universityName.toUpperCase()}
              </h3>
              <span className="absolute left-0 top-full hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 w-max max-w-xs shadow-lg">
                {card.universityName.toUpperCase()}
              </span>
            </div>

            <p className="text-sm text-gray-500">{card.Location}</p>
          </div>
        </div>
        <div className="space-y-4">
          <TruncatedTextWithHover
            text={card.Qualification}
            className="text-sm text-gray-500"
          />
          <TruncatedTextWithHover
            text={card.programName}
            className="font-medium text-base uppercase"
          />
          <div className="flex flex-wrap gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors cursor-default">
              {card.Duration}
            </span>
          </div>
          <InfoRow label="Location" value={card.Location} />
          <InfoRow label="Tuition Fees" value={card.Fees.replace(/USD|GBP|AED/g, (match) => {
            return match === "USD" ? "$" : match === "GBP" ? "£" : "د.إ";
          })} />
          <InfoRow label="Entry Score (IELTS)" value={card["Entry Score"]} />
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsDialogOpen(true)}
              className="w-full mr-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Check Details
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <SaveHeart programId={card._id || card.cardId} size="md" />
          </div>
        </div>
      </div>

      <ProgramDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        card={card}
      />
    </>
  );
};

const ProgramDialog = ({ isOpen, onClose, card }) => {
  const [activeTab, setActiveTab] = React.useState('about');
  const { appliedPrograms } = useAppliedPrograms();
  const countryInfo = countryData.find(country => country.country === card.Location);
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false);
  
  // Check if the program has been applied to
  const isApplied = appliedPrograms[card._id || card.cardId] || false;

  const tabs = [
    { id: 'about', label: 'About the Course' },
    { id: 'apply', label: 'eligibility Criteria' },
    { id: 'others', label: 'Country Information' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} >
      <div className="max-h-[90vh]  overflow-y-auto p-3 sm:p-6">
        {/* Header - Made more compact on mobile */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 ">

              <img
                src={card.imageSrc}
                alt={card.universityName.toUpperCase()}
                className="w-full h-full object-cover"
              />

            </div>

            <div className="flex-1">
              <h2 className="text-base sm:text-xl font-bold leading-tight">{card.universityName.toUpperCase()}</h2>
              <p className="text-gray-500 text-sm">{card.Location}</p>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors sm:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={onClose}
            className="hidden sm:block p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-sm sm:text-lg uppercase">{card.programName}</h3>
            {!isApplied && <SaveHeart programId={card._id || card.cardId} />}
            {isApplied && (
              <span className="px-3 py-1 text-xs sm:text-sm bg-green-100 text-green-600 rounded-full font-medium">
                Applied
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <span className="px-3 py-1 text-xs sm:text-sm bg-blue-100 text-blue-600 rounded-full">
              Qualification: {card.Qualification}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-green-100 text-green-600 rounded-full">
              Duration: {card.Duration}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-purple-100 text-purple-600 rounded-full">
              Tuition Fees: {card.Fees.replace(/USD|GBP|AED/g, (match) => {
                return match === "USD" ? "$" : match === "GBP" ? "£" : "د.إ";
              })}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-yellow-100 text-yellow-600 rounded-full">
              Entry Score (IELTS): {card["Entry Score"]}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-red-100 text-red-600 rounded-full flex items-center" >
              Career Suitability: {card.careerSuitability}
              {card.careerSuitability && <FaCheckCircle className="ml-1 text-gray-500" />}
            </span>
            <span className="px-3 py-1 text-xs sm:text-sm bg-gray-100 text-gray-600 rounded-full flex items-center">
              Admission Probability: {card.admissionProbability}
              {card.admissionProbability && <FaCheckCircle className="ml-1 text-gray-500" />}
            </span>
            {!isApplied && (
              <>
                <button className="bg-blue-500 text-xs lg:text-base rounded-lg px-3 lg:px-6 py-1 lg:py-0  shadow  transition font-semibold text-white  sm:w-auto flex items-center gap-2 "
                  onClick={() => window.open("https://calendly.com/applicationuni", "_blank")}>
                  Book Call
                </button>
                <button
                  onClick={() => setIsApplicationFormOpen(true)}
                  className="bg-blue-500 text-xs lg:text-base rounded-lg px-3 lg:px-6 py-1 lg:py-0 shadow transition font-semibold text-white  sm:w-auto flex items-center gap-2"
                >
                  Apply Now
                </button>
              </>
            )}
          </div>
        </div>


        {/* Tabs Navigation - Made scrollable on mobile */}
        <div className="border-b mt-4 sm:mt-6">
          <nav className="-mb-px flex space-x-4 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 text-xs sm:text-base whitespace-nowrap transition-colors flex-shrink-0 ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content - Adjusted spacing and font sizes */}
        <div className="mt-4 sm:mt-6 text-sm sm:text-base h-[60vh] ">
          {activeTab === 'about' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-3 sm:space-y-4">
                <h4 className="font-medium text-sm sm:text-lg">Program Overview</h4>
                <p className="text-gray-600 text-sm sm:text-base">{card["About the course"]}</p>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-sm sm:text-lg">Key Highlights</h4>
                <ul className="list-disc pl-4 space-y-1 text-gray-600 text-sm sm:text-base">
                  <li>Industry-relevant curriculum</li>
                  <li>Expert faculty members</li>
                  <li>Practical training and internships</li>
                  <li>State-of-the-art facilities</li>
                </ul>
              </div>

              {card.skillRecommendation && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm sm:text-lg">Recommended Skills</h4>
                  <div className="rounded-lg">
                    <div className="flex items-start gap-3">
                      <div>
                        <p className="text-sm sm:text-base">{card.skillRecommendation}</p>
                        <p className="text-xs sm:text-sm text-blue-700 mt-2 mb-5">
                          ⚠️ AI-generated recommendation based on program curriculum and industry trends
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
            </div>
          )}

          {activeTab === 'apply' && (
            <div className="space-y-4 sm:space-y-6 ">
              <h4 className="font-medium text-sm sm:text-lg">Application Process</h4>
              <p className="text-gray-600 text-sm sm:text-base">{card.parsed_output.entry_requirements_text}</p>
              
            </div>
          )}

          {activeTab === 'others' && countryInfo && (
            <div className="space-y-6 sm:space-y-8  ">
              {[
                'Living Cost',
                'Weather',
                { display: 'Part-time Work Opportunities', key: 'part_time_work' }
              ].map((section, index) => {
                // Handle both string and object section formats
                const displayText = typeof section === 'object' ? section.display : section;
                const key = typeof section === 'object' ?
                  section.key :
                  section.toLowerCase().replace(/ /g, '_');

                return (
                  <div key={index}>
                    <h4 className="font-medium text-sm sm:text-lg mb-2 sm:mb-3">{displayText}</h4>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {countryInfo[key]}
                    </p>
                  </div>
                );
              })}

              <div>
                <h4 className="font-medium text-sm sm:text-lg mb-2 sm:mb-3">Post-Study Visa</h4>
                <p className="text-gray-600 text-sm sm:text-base">{card.parsed_output.post_study_visa}</p>
              </div>

              <div>
                <h4 className="font-medium text-sm sm:text-lg mb-2 sm:mb-3">Career Options</h4>
                <div className="flex flex-wrap gap-2">
                  {card.parsed_output.career_options.split(',').map((career, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full text-xs sm:text-sm font-medium mb-5"
                    >
                      {career.trim()}
                    </span>
                  ))}
                </div>
              </div>
             
            </div>
          )}

          {activeTab === 'others' && !countryInfo && (
            <div className="text-gray-600 text-sm sm:text-base">
              Country information not available.

            </div>

          )}

        </div>
        <ApplicationForm
          isOpen={isApplicationFormOpen}
          onClose={() => setIsApplicationFormOpen(false)}
          programId={card._id || card.cardId}
          programName={card.programName}
          universityName={card.universityName}
          onApplicationSubmit={fetchSavedPrograms}
        />

      </div>

    </Modal>
  );
};

const InfoRow = ({ label, value, showIcon }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const getTooltipContent = (label) => {
    if (label === "Career Suitability") {
      return (
        <div>
          <span>Career Suitability Score (1-10): Indicates how well this program aligns with current job market demands and career opportunities. 1-3: Limited prospects, 4-6: Moderate prospects, 7-10: Strong prospects</span> <br />
          <span style={{ color: "yellow" }}>
            ⚠️  This score is AI generated.
          </span>
        </div>
      )
    }
    if (label === "Admission Probability") {
      return (

        <div>
          <span>Admission Probability Score (1-10): Based on your academic profile compared to historical admission data. 1-3: Low chance, 4-6: Moderate chance, 7-10: High chance</span> <br />
          <span style={{ color: "yellow" }}>
            ⚠️  This score is AI generated.
          </span>
        </div>

      );
    }
    return "";
  };

  const isScoreType = label === "Career Suitability" || label === "Admission Probability";

  return (
    <div className="flex justify-between text-sm">
      <div className="flex items-center gap-1.5">
        <span className="text-gray-500">{label}</span>
        {showIcon && (
          <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <InfoIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
            {showTooltip && (
              <div className="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10">
                {getTooltipContent(label)}
                <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
              </div>
            )}
          </div>
        )}
      </div>
      <span className={isScoreType ? "font-semibold" : ""}>
        {isScoreType ? `${value}` : value}
      </span>
    </div>
  );
};

let fetchSavedPrograms;

const SavedApplications = () => {
  const { user } = useUser();
  const [savedPrograms, setSavedPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedProgramsMap, setSavedProgramsMap] = useState({});
  const { appliedPrograms } = useAppliedPrograms();

  const setSavedStatus = (programId, isSaved) => {
    setSavedProgramsMap(prev => ({
      ...prev,
      [programId]: isSaved
    }));
  };

  useEffect(() => {
    fetchSavedPrograms();
  }, [user]);

  fetchSavedPrograms = async () => {
    try {
      setLoading(true);
      const token = user?.token || localStorage.getItem('token');
      
      if (!user?.id) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/saved-programs/${user.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        setSavedPrograms(response.data.savedPrograms);
        
        // Initialize savedProgramsMap with saved programs
        const programsMap = {};
        response.data.savedPrograms.forEach(program => {
          programsMap[program._id || program.cardId] = true;
        });
        setSavedProgramsMap(programsMap);
      }
    } catch (error) {
      console.error('Error fetching saved programs:', error);
      setError('Failed to load saved programs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 text-blue-600 hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (savedPrograms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8">
        <div className="text-blue-600 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">No Saved Programs</h2>
        <p className="text-gray-600 mb-6">
          You haven't saved any programs yet. Browse programs and save them for later.
        </p>
        <a 
          href="/programs" 
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Browse Programs
        </a>
      </div>
    );
  }

  return (
    <SavedProgramsContext.Provider value={{ savedPrograms: savedProgramsMap, setSavedStatus }}>
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Saved Programs</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 place-items-center">
          {savedPrograms.map((program) => (
            <UniversityCard 
              key={program._id || program.cardId} 
              card={program}
              isApplied={appliedPrograms[program._id || program.cardId] || false}
            />
          ))}
        </div>
      </div>
    </SavedProgramsContext.Provider>
  );
};

export default SavedApplications; 